import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Loader2 } from 'lucide-react';

export function ClientAccessDialog({
  open, onOpenChange, clients, allUsers, selectedClientId, setSelectedClientId,
  selectedViewers, toggleViewer, onSave, saving, currentUserId,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Project Access Management</DialogTitle>
          <DialogDescription>
            Assign a client and grant additional users access to this project
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label className="form-label">Primary Client (Owner)</Label>
            <Select
              value={selectedClientId || 'none'}
              onValueChange={(value) => setSelectedClientId(value === 'none' ? '' : value)}
            >
              <SelectTrigger data-testid="select-primary-client">
                <SelectValue placeholder="Select a client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Client Assigned</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.user_id} value={client.user_id}>
                    {client.name} ({client.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The primary client will always have access to this project
            </p>
          </div>

          <div className="space-y-2">
            <Label className="form-label">Additional Viewers (Optional)</Label>
            <p className="text-xs text-muted-foreground mb-3">
              Grant other staff or clients access to view this project
            </p>
            <div className="border border-border rounded-md max-h-56 overflow-y-auto">
              {allUsers.filter(u => u.user_id !== selectedClientId && u.user_id !== currentUserId && u.role !== 'admin').length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground text-center">
                  No other users available
                </p>
              ) : (
                allUsers
                  .filter(u => u.user_id !== selectedClientId && u.user_id !== currentUserId && u.role !== 'admin')
                  .map(u => (
                    <div
                      key={u.user_id}
                      className="flex items-center gap-3 p-3 border-b border-border last:border-b-0 hover:bg-muted/50"
                      data-testid={`viewer-option-${u.user_id}`}
                    >
                      <Checkbox
                        id={`viewer-${u.user_id}`}
                        checked={selectedViewers.includes(u.user_id)}
                        onCheckedChange={() => toggleViewer(u.user_id)}
                      />
                      <Label
                        htmlFor={`viewer-${u.user_id}`}
                        className="flex-1 cursor-pointer text-sm font-normal"
                      >
                        {u.name}
                        <span className="text-muted-foreground ml-2">({u.role})</span>
                      </Label>
                    </div>
                  ))
              )}
            </div>
            {selectedViewers.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {selectedViewers.length} additional viewer(s) selected
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            className="btn-primary"
            disabled={saving}
            data-testid="save-client-assignment-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Assignment'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
